import React from "react";
import { FmBanner } from "./FmBanner";
import { FmButton } from "./FmButton";
import { FmErrorBoundary } from "./FmErrorBoundary";
import { FmHeading } from "./FmComposition";
import { FmSplit } from "./FmLayout";
import { FmSectionBand } from "./FmReportComposition";
import { FmElementActionInhibitionProvider } from "./elements/elementInhibition";
import { FmHostedHeadingProvider, hostedHeadingFor } from "./fmHostedHeading";
import type {
  FmOperationalRegionDescriptor,
  FmOperationalWorkspaceDescriptor,
} from "./FmOperationalWorkspaceRoot.types";
import type { OperationalActionActivate } from "./FmOperationalWorkspaceStateSurfaces";
import { RegionSurface } from "./FmOperationalWorkspaceRegions.content";
import { validateSection } from "./sections/section.validation";
import { validateHandoffControlReferences } from "./FmOperationalWorkspaceRegion.validation";
import { assertArchetypePaceCapabilities } from "./sections/paceCapabilities";
import {
  regionBody,
  regionOwnsItsHeading,
  regionUsesStoryRail,
  type RegionBody,
} from "./FmOperationalWorkspaceRegions.helpers";

/**
 * The region's SKELETON while the workspace loads: the story rail at its real
 * scale over a neutral block at the media's real aspect, so nothing jumps and the
 * owner can already read the structure. A bare 24px spinner would leave a
 * ~1345x300px void that then jumps to ~2600px of regions
 */
function RegionSkeletonSurface({
  region,
}: {
  region: FmOperationalRegionDescriptor;
}): React.ReactElement {
  return (
    <div
      className="fm-operational-workspace__region-skeleton"
      data-kind={region.kind}
      data-testid={`operational-region-skeleton-${region.id}`}
      aria-hidden="true"
    />
  );
}

/**
 * The ONE non-ready surface a region owns: the same toned notice the workspace
 * scope uses, on the region rail, naming the missing thing and carrying the
 * control that produces it. It replaces a family of centred grey sentences of
 * the form "<region title> is unavailable.".
 */
function RegionUnavailableSurface({
  descriptor,
  region,
  onActivate,
}: {
  descriptor: FmOperationalWorkspaceDescriptor;
  region: FmOperationalRegionDescriptor;
  onActivate?: OperationalActionActivate;
}): React.ReactElement {
  const resolvingActionId = region.unavailable.resolvingActionId;
  const action = resolvingActionId
    ? descriptor.controls.find((control) => control.id === resolvingActionId)
    : undefined;
  return (
    <FmBanner
      tone="info"
      title={region.unavailable.title}
      description={region.unavailable.reason}
      action={
        action && onActivate ? (
          <FmButton
            variant={action.variant ?? "primary"}
            size={action.size}
            disabled={action.disabled}
            title={action.disabledReason}
            onClick={(event) => onActivate(action, event.currentTarget)}
            data-testid={`operational-action-${action.id}`}
          >
            {action.label}
          </FmButton>
        ) : undefined
      }
      data-testid={`operational-region-unavailable-${region.id}`}
    />
  );
}

type OperationalWorkspaceRegionProps = {
  descriptor: FmOperationalWorkspaceDescriptor;
  region: FmOperationalRegionDescriptor;
  dominant: boolean;
  /** The guided step's question is this region's heading, so the region prints none. */
  headedByStep?: boolean;
  inhibitionReason: string | null;
  onActivate?: OperationalActionActivate;
  paidBoundary?: React.ReactNode;
  stateSurface?: React.ReactNode;
  stateSurfaceMode?: "replace" | "lead";
};

function RegionHeading({
  region,
  headedByStep,
  replaced,
  unavailable,
}: Pick<OperationalWorkspaceRegionProps, "region" | "headedByStep"> &
  Pick<RegionBody, "replaced" | "unavailable">): React.ReactElement | null {
  if (headedByStep || regionOwnsItsHeading(region)) return null;
  return (
    <header className="fm-operational-workspace__region-heading">
      {region.eyebrow ? <span>{region.eyebrow}</span> : null}
      <FmHeading level={3}>{region.title}</FmHeading>
      {region.lede && !replaced && !unavailable ? (
        <p
          className="fm-operational-workspace__region-lede"
          data-testid={`operational-region-${region.id}-lede`}
        >
          {region.lede}
        </p>
      ) : null}
    </header>
  );
}

/**
 * Region content publishes the region title as the host heading, so an element
 * repeating it drops its own copy, and the workspace's inhibition, so element
 * actions refuse while a save or a stale revision guards the plan.
 */
function RegionContent({
  descriptor,
  region,
  inhibitionReason,
  onActivate,
  paidBoundary,
  stateSurface,
  body,
}: Pick<
  OperationalWorkspaceRegionProps,
  | "descriptor"
  | "region"
  | "inhibitionReason"
  | "onActivate"
  | "paidBoundary"
  | "stateSurface"
> & {
  body: RegionBody;
}): React.ReactElement {
  const { replaced, loading, unavailable } = body;
  // A section that prints the region heading itself must not find it hosted.
  const host = regionOwnsItsHeading(region) ? null : hostedHeadingFor(region.title, 3);
  return (
    <FmHostedHeadingProvider value={host}>
    <FmElementActionInhibitionProvider value={inhibitionReason}>
    <div className="fm-operational-workspace__region-content">
      {stateSurface}
      {paidBoundary}
      {unavailable ? (
        <RegionUnavailableSurface
          descriptor={descriptor}
          region={region}
          onActivate={onActivate}
        />
      ) : null}
      {loading ? <RegionSkeletonSurface region={region} /> : null}
      {replaced || unavailable || loading ? null : (
        <RegionSurface descriptor={descriptor} region={region} />
      )}
    </div>
    </FmElementActionInhibitionProvider>
    </FmHostedHeadingProvider>
  );
}

function OperationalWorkspaceRegionSurface({
  props,
  body,
}: {
  props: OperationalWorkspaceRegionProps;
  body: RegionBody;
}): React.ReactElement {
  const { region, dominant, headedByStep, stateSurface } = props;
  const { replaced, loading, unavailable } = body;
  const presentation =
    region.kind === "section" && region.section.arrangement === "carousel"
      ? "highlight"
      : undefined;
  const rail =
    !headedByStep && regionUsesStoryRail(region, replaced, unavailable)
      ? "story"
      : "none";
  /* The section shell owns the band: its tone, background and vertical rhythm.
     The region inside it carries the archetype's own state and test hooks. */
  return (
    <FmSectionBand
      tone={region.bandTone ?? "light"}
      width="full"
      rail="wide"
      className="fm-operational-workspace__region-band"
    >
    <section
      className="fm-operational-workspace__region"
      data-kind={region.kind}
      data-presentation={presentation}
      data-band-tone={region.bandTone ?? "light"}
      data-dominant={dominant || undefined}
      data-rail={rail}
      data-loading={loading ? "" : undefined}
      data-state-surface={stateSurface ? "" : undefined}
      data-unavailable-reason={
        unavailable ? (region.unavailable.reasonId ?? "unnamed") : undefined
      }
      aria-label={region.title}
      data-testid={`operational-region-${region.id}`}
    >
      <div className="fm-operational-workspace__region-inner">
        {rail === "story" ? (
          /* The heading and its lede read as the narrow rail beside the
             evidence; a map's rail stretches beside the full-height frame. */
          <FmSplit
            balance="copy-narrow"
            align="start"
            contentSurface="plain"
            className="fm-operational-workspace__region-split"
            content={
              <RegionHeading
                region={region}
                replaced={replaced}
                unavailable={unavailable}
              />
            }
            graphic={<RegionContent {...props} body={body} />}
          />
        ) : (
          <>
            <RegionHeading
              region={region}
              headedByStep={headedByStep}
              replaced={replaced}
              unavailable={unavailable}
            />
            <RegionContent {...props} body={body} />
          </>
        )}
      </div>
    </section>
    </FmSectionBand>
  );
}

/* Each served region object gets its own reset key, so a region that failed
   recovers as soon as a new descriptor serves it again, without remounting a
   healthy region on every render. */
const regionResetKeys = new WeakMap<FmOperationalRegionDescriptor, number>();
let nextRegionResetKey = 0;

function regionResetKey(region: FmOperationalRegionDescriptor): number {
  const known = regionResetKeys.get(region);
  if (known !== undefined) return known;
  nextRegionResetKey += 1;
  regionResetKeys.set(region, nextRegionResetKey);
  return nextRegionResetKey;
}

function ValidatedRegion(props: OperationalWorkspaceRegionProps): React.ReactElement {
  /* The root checks only the workspace shell. The section contract is checked
     here, inside the region's boundary and before any helper reads the section,
     so a broken section fails its own region in every region state. */
  validateHandoffControlReferences(
    props.region,
    `region "${props.region.id}"`,
    props.descriptor.controls,
  );
  if (props.region.kind === "section") {
    validateSection(props.region.section, `region "${props.region.id}".section`);
    assertArchetypePaceCapabilities(
      "FmOperationalWorkspaceRoot",
      props.region.section,
      `region "${props.region.id}".section`,
    );
  }
  /* ONE predicate for "the state surface took this region's place". Deciding the
     rail, the lede and the content separately let them drift: a descriptor with
     `stateSurfaceMode: 'replace'` and no surface hid the lede while still
     rendering the evidence it describes. */
  return (
    <OperationalWorkspaceRegionSurface props={props} body={regionBody(props)} />
  );
}

export function OperationalWorkspaceRegion(
  props: OperationalWorkspaceRegionProps,
): React.ReactElement {
  const { descriptor, region } = props;
  return (
    <FmErrorBoundary
      title={descriptor.vocabulary.regionFailure.title}
      description={descriptor.vocabulary.regionFailure.description}
      resetKey={regionResetKey(region)}
      data-testid={`operational-region-error-${region.id}`}
    >
      <ValidatedRegion {...props} />
    </FmErrorBoundary>
  );
}
